import { ButtonStyled } from "components/buttons/buttons.styled";
import { H4 } from "components/Typography/text.styled";
import { FontFamily } from "config/font";
import { SubscriptionSettingsFormData } from "model/Company";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Api } from "services/api/api.service";
import { useAlertStore } from "stores/alertStore/alertStore";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { ToggleWithLabel } from "../../general/toggle/toggleWithLabelProps.component";
import { toBool } from "../companySettingsMobileView/companySystemSettings/companySystemSettings.component";
import { SettingsSection } from "../companySettingsMobileView/companySystemSettings/settingSection.component";

const DEFAULT_FORM: SubscriptionSettingsFormData = {
  companyShowInconvenientModule: false,
  companyShowStampclockModule: false,
  companyShowChatModule: false,
  companyShowTipModule: false,
  companyShowTeamGigModule: false,
  companyShowPublicGigModule: false,
  enableCompanySalaryView: false,
};

type SettingKey =
  | "CompanyShowInconvenientModule"
  | "CompanyShowStampclockModule"
  | "CompanyShowChatModule"
  | "CompanyShowTipModule"
  | "CompanyShowTeamGigModule"
  | "CompanyShowPublicGigModule"
  | "EnableCompanySalaryView";

export const CompanySubscriptionSettings: React.FC = () => {
  const { t } = useTranslation();
  const [companyState, , companyDispatch] = useCompanyStore();
  const [formData, setFormData] =
    useState<SubscriptionSettingsFormData>(DEFAULT_FORM);
  const savedSettingsRef = useRef<SubscriptionSettingsFormData>(DEFAULT_FORM);
  const [, , alertDispatch] = useAlertStore();

  const showAlert = useCallback(
    (error?: boolean) => {
      alertDispatch({
        type: "SHOW_ALERT",
        payload: !error
          ? {
              icon: "checkmark",
              title: t("Alerts.Updated"),
              message: t("CompanySubscription.SettingsUpdated"),
            }
          : {
              icon: "alert",
              title: t("Alerts.OhNo"),
              message: t("CompanySubscription.SettingsNotUpdated"),
            },
      });
    },
    [alertDispatch, t]
  );

  useEffect(() => {
    if (!companyState.company?.id) return;

    let cancelled = false;

    Api()
      .company.getCompanySettings(companyState.company.id)
      .then((res) => {
        if (cancelled) return;

        const get = (k: string): string | undefined =>
          res.data.find((s: { key: string; value: string }) => s.key === k)
            ?.value;

        const init: SubscriptionSettingsFormData = {
          companyShowInconvenientModule: toBool(
            get("CompanyShowInconvenientModule"),
            false
          ),
          companyShowStampclockModule: toBool(
            get("CompanyShowStampclockModule"),
            false
          ),
          companyShowChatModule: toBool(get("CompanyShowChatModule"), false),
          companyShowTipModule: toBool(get("CompanyShowTipModule"), false),
          companyShowTeamGigModule: toBool(
            get("CompanyShowTeamGigModule"),
            false
          ),
          companyShowPublicGigModule: toBool(
            get("CompanyShowPublicGigModule"),
            false
          ),
          enableCompanySalaryView: toBool(
            get("EnableCompanySalaryView"),
            false
          ),
        };

        savedSettingsRef.current = init;
        setFormData(init);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [companyState.company?.id]);

  const setToggle =
    (key: keyof SubscriptionSettingsFormData) => (value: boolean) => {
      setFormData((prev) => ({ ...prev, [key]: value }));
    };

  const diffPlan: {
    key: SettingKey;
    pick: (f: SubscriptionSettingsFormData) => unknown;
    toBackendString: (v: unknown) => string;
  }[] = [
    {
      key: "CompanyShowInconvenientModule",
      pick: (f) => f.companyShowInconvenientModule,
      toBackendString: (v) => String(Boolean(v)),
    },
    {
      key: "CompanyShowStampclockModule",
      pick: (f) => f.companyShowStampclockModule,
      toBackendString: (v) => String(Boolean(v)),
    },
    {
      key: "CompanyShowChatModule",
      pick: (f) => f.companyShowChatModule,
      toBackendString: (v) => String(Boolean(v)),
    },
    {
      key: "CompanyShowTipModule",
      pick: (f) => f.companyShowTipModule,
      toBackendString: (v) => String(Boolean(v)),
    },
    {
      key: "CompanyShowTeamGigModule",
      pick: (f) => f.companyShowTeamGigModule,
      toBackendString: (v) => String(Boolean(v)),
    },
    {
      key: "CompanyShowPublicGigModule",
      pick: (f) => f.companyShowPublicGigModule,
      toBackendString: (v) => String(Boolean(v)),
    },
    {
      key: "EnableCompanySalaryView",
      pick: (f) => f.enableCompanySalaryView,
      toBackendString: (v) => String(Boolean(v)),
    },
  ];

  const changedSettings = useMemo(() => {
    const current = formData;
    const baseline = savedSettingsRef.current;

    return diffPlan
      .map(({ key, pick, toBackendString }) => {
        const curS = toBackendString(pick(current));
        const baseS = toBackendString(pick(baseline));
        return curS !== baseS ? { key, value: curS } : null;
      })
      .filter(Boolean) as { key: SettingKey; value: string }[];
  }, [formData]);

  const madeChanges = changedSettings.length > 0;

  const handleUpdate = useCallback(() => {
    if (!companyState.company?.id || changedSettings.length === 0) return;

    Promise.all(
      changedSettings.map((s) =>
        Api().company.systemSettings.updateCompanySetting(
          companyState.company!.id,
          s.key,
          s.value
        )
      )
    )
      .then(() => {
        changedSettings.forEach((s) => {
          companyDispatch({
            type: "UPDATE_COMPANY_SETTINGS",
            payload: { key: s.key, value: s.value },
          });
        });

        savedSettingsRef.current = { ...formData };
        setFormData((prev) => ({ ...prev }));
        showAlert();
      })
      .catch(() => {
        showAlert(true);
      });
  }, [
    companyState.company?.id,
    changedSettings,
    companyDispatch,
    formData,
    showAlert,
  ]);

  return (
    <>
      <H4 fontFamily={FontFamily.LeagueSpartanBold}>
        {t("CompanySubscription.ActivateModules")}
      </H4>

      <SettingsSection title={t("CompanySubscription.OBModule")}>
        <ToggleWithLabel
          label={t("CompanySubscription.HandleOB")}
          leftLabel={t("CompanySubscription.Inactive")}
          rightLabel={t("CompanySubscription.Active")}
          value={formData.companyShowInconvenientModule}
          onChange={setToggle("companyShowInconvenientModule")}
        />
      </SettingsSection>

      <SettingsSection title={t("CompanySubscription.SalaryModule")}>
        <ToggleWithLabel
          label={t("CompanySubscription.HandleSalary")}
          leftLabel={t("CompanySubscription.Inactive")}
          rightLabel={t("CompanySubscription.Active")}
          value={formData.enableCompanySalaryView}
          onChange={setToggle("enableCompanySalaryView")}
        />
      </SettingsSection>

      <SettingsSection title={t("CompanySubscription.StampClockModule")}>
        <ToggleWithLabel
          label={t("CompanySubscription.HandleStampClock")}
          leftLabel={t("CompanySubscription.Inactive")}
          rightLabel={t("CompanySubscription.Active")}
          value={formData.companyShowStampclockModule}
          onChange={setToggle("companyShowStampclockModule")}
        />
      </SettingsSection>

      <SettingsSection title={t("CompanySubscription.TeamGigModule")}>
        <ToggleWithLabel
          label={t("CompanySubscription.HandleTeamGig")}
          leftLabel={t("CompanySubscription.Inactive")}
          rightLabel={t("CompanySubscription.Active")}
          value={formData.companyShowTeamGigModule}
          onChange={setToggle("companyShowTeamGigModule")}
        />
      </SettingsSection>

      <SettingsSection title={t("CompanySubscription.PublicGigModule")}>
        <ToggleWithLabel
          label={t("CompanySubscription.HandlePublicGig")}
          leftLabel={t("CompanySubscription.Inactive")}
          rightLabel={t("CompanySubscription.Active")}
          value={formData.companyShowPublicGigModule}
          onChange={setToggle("companyShowPublicGigModule")}
        />
      </SettingsSection>

      <SettingsSection title={t("CompanySubscription.ChatModule")}>
        <ToggleWithLabel
          label={t("CompanySubscription.HandleChat")}
          leftLabel={t("CompanySubscription.Inactive")}
          rightLabel={t("CompanySubscription.Active")}
          value={formData.companyShowChatModule}
          onChange={setToggle("companyShowChatModule")}
        />
      </SettingsSection>

      <SettingsSection title={t("CompanySubscription.TipModule")}>
        <ToggleWithLabel
          label={t("CompanySubscription.HandleTip")}
          leftLabel={t("CompanySubscription.Inactive")}
          rightLabel={t("CompanySubscription.Active")}
          value={formData.companyShowTipModule}
          onChange={setToggle("companyShowTipModule")}
        />
        <ButtonStyled disabled={!madeChanges} onClick={handleUpdate}>
          {t("General.Save")}
        </ButtonStyled>
      </SettingsSection>
    </>
  );
};
